import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faRotateLeft,
  faPlay,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

const ProgressModal = ({ 
  isOpen, 
  onClose, 
  onStartOver, 
  onResume, 
  title, 
  message, 
  theme 
}) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    iconWrapper: {
      width: '4rem',
      height: '4rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[400]}, ${theme?.primary?.blue?.[600]})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      fontSize: '2rem',
      color: 'white',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    message: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    button: {
      padding: '0.875rem 1.5rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]} 0%, ${theme?.primary?.blue?.[600]} 100%)`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.blue?.[500]}30`,
    },
    secondaryButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: theme?.shadows?.lg,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.closeButton}
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <motion.div
              style={styles.iconWrapper}
              animate={{ 
                rotate: [0, -10, 10, -10, 0],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 2,
              }}
            >
              <FontAwesomeIcon icon={faHistory} />
            </motion.div>
            
            <h2 style={styles.title}>{title || 'Resume Progress?'}</h2>
            
            <p style={styles.message}>
              {message || 'You have existing progress in this topic. Would you like to continue where you left off or start from the beginning?'}
            </p>
            
            <div style={styles.buttonContainer}>
              <motion.button
                style={{ ...styles.button, ...styles.primaryButton }}
                whileHover={styles.buttonHover}
                whileTap={{ scale: 0.95 }}
                onClick={onResume}
              >
                <FontAwesomeIcon icon={faPlay} />
                Resume Where I Left Off
              </motion.button>
              
              <motion.button
                style={{ ...styles.button, ...styles.secondaryButton }}
                whileHover={styles.buttonHover}
                whileTap={{ scale: 0.95 }}
                onClick={onStartOver}
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                Start From Beginning
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProgressModal;